<template>
  <section class="best-team-section">
    <h3>Meilleure équipe à aligner</h3>

    <div v-if="isLoading" class="loading placeholder-glow" aria-hidden="true">
      <span class="placeholder col-12" />
      <span class="placeholder col-10" />
      <span class="placeholder col-11" />
    </div>

    <div v-else-if="!bestTeam || !bestTeam.length" class="no-data">
      <p>Aucune donnée disponible pour calculer la meilleure équipe.</p>
    </div>

    <team-display v-else :players="bestTeam" />
  </section>
</template>

<script setup>
import { toRef } from "vue";

import TeamDisplay from "@/components/team/TeamDisplay.vue";
import { useLeagueCache } from "@/use/useLeagueCache";
import { useMPG } from "@/use/useMPG";
import { calculateBestTeam } from "@/utils/bestTeam/bestTeamCalculator";
import { getDivisionId } from "@/utils/leagues/leagueContext";

const props = defineProps({
  league: {
    type: Object,
    required: true,
  },
});

const { getTeamPlayersPerformances, getChampionshipPlayersPool } = useMPG();

const CHAMPIONNATS_NATIONAUX = [1, 2, 3]; // Ligue 1, Premier League, Liga

/**
 * Récupère le pool de joueurs pour un championnat
 * Pour les compétitions européennes, fusionne les pools des championnats nationaux
 * @param {Number} championshipId l'identifiant du championnat
 * @returns {Promise<Map>} les joueurs indexés par identifiant
 */
const getPlayersPoolForChampionship = async (championshipId) => {
  const playersPool = new Map();

  const mainPool = await getChampionshipPlayersPool(championshipId);
  mainPool.forEach((value, key) => playersPool.set(key, value));

  // Pour les compétitions européennes (championshipId >= 4),
  // ajouter les pools des championnats nationaux
  if (championshipId >= 4) {
    const nationalPools = await Promise.all(
      CHAMPIONNATS_NATIONAUX.map(id => getChampionshipPlayersPool(id)),
    );
    nationalPools.forEach(pool => {
      pool.forEach((value, key) => playersPool.set(key, value));
    });
  }

  return playersPool;
};

/**
 * Renvoi l'identifiant du match de l'utilisateur pour la journée
 * Le match live est privilégié ; hors live, il est retrouvé dans la journée à venir
 * en comparant le numéro d'équipe présent dans les identifiants
 * @param {Object} league la ligue issue des données live
 * @returns {String|undefined} l'identifiant du match
 */
const getUserMatchId = (league) => {
  const liveMatches = league.liveState?.liveMatches;
  if (liveMatches) {
    const userMatch = Object.values(liveMatches).find(
      match => match.home.teamId === league.userTeamId || match.away.teamId === league.userTeamId,
    );
    if (userMatch) {
      return userMatch.id;
    }
  }

  const userTeamNumber = league.userTeamId?.split("_").pop();
  return league.nextDivisionGameWeek?.matchesIds?.find(id => {
    const parts = id.split("_");
    const homeTeamNumber = parts[parts.length - 2];
    const awayTeamNumber = parts[parts.length - 1];
    return homeTeamNumber === userTeamNumber || awayTeamNumber === userTeamNumber;
  });
};

/**
 * Renvoi la meilleure équipe d'une ligue, ou undefined faute de données
 * @param {Object} league la ligue issue des données live
 * @returns {Promise<Array|undefined>} les onze joueurs
 */
const getLeagueBestTeam = async (league) => {
  const divisionId = getDivisionId(league);
  const matchId = getUserMatchId(league);
  if (! divisionId || ! league.userTeamId || ! matchId) {
    return undefined;
  }

  const playersPerformances = await getTeamPlayersPerformances(divisionId, matchId, league.userTeamId);
  if (! playersPerformances) {
    return undefined;
  }

  const playersPool = await getPlayersPoolForChampionship(league.championshipId || 1);
  return calculateBestTeam(playersPerformances, playersPool);
};

// Un échec de chargement affiche le message d'absence de données plutôt que
// de faire planter l'écran ; il n'est pas mis en cache, pour rester
// réessayable au prochain passage sur la ligue
const league = toRef(props, "league");
const { isLoading, value: bestTeam } = useLeagueCache(league, getLeagueBestTeam, "meilleure équipe");
</script>

<style lang="scss" scoped>
.best-team-section {
  padding: 20px 0;

  h3 {
    text-align: center;
    margin-bottom: 15px;
    font-size: 1.1rem;
  }
}

.loading, .no-data {
  text-align: center;
  padding: 40px 20px;
}

.loading {
  display: flex;
  flex-direction: column;
  gap: 8px;

  .placeholder {
    height: 40px;
    border-radius: 4px;
  }
}

.no-data {
  color: #666;
}
</style>